import { type CSSProperties, type MouseEvent } from 'react';
import type { CommonFolderTreeProps, FolderStruct } from '../types';
import { FolderWithTrailing } from './FolderWithTrailing';
import { Icon } from '../../Icon';
import { Typography } from '../../Typography/Typography';

export const TreeFolder = <T,>({
  folderStructure,
  selectedFolderId,
  onChangeSelected,
  expandedFolderIds,
  onChangeExpanded,
  renderTrailing,
  maxWidth,
}: { folderStructure: FolderStruct<T> } & CommonFolderTreeProps<T>) => {
  const { id, name, count, subfolders, descendantIds, level } = folderStructure;
  const isCurrentDirectory = selectedFolderId === id;
  const isCurrentDirectoryPredecessor =
    !!selectedFolderId && descendantIds.has(selectedFolderId);

  const isExpanded = !!expandedFolderIds?.has(id);

  const handleClickFolder = (event: MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();
    if (!isCurrentDirectory) {
      onChangeSelected?.(folderStructure);
    }
  };

  const handleClickCaret = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    const nextExpandedFolderIds =
      expandedFolderIds?.symmetricDifference(new Set([id])) ?? new Set([id]);

    // If we're collapsing the folder, we also want to collapse all its descendants
    onChangeExpanded?.(
      nextExpandedFolderIds.has(id)
        ? nextExpandedFolderIds
        : nextExpandedFolderIds.difference(descendantIds),
      folderStructure
    );
  };

  return (
    <>
      <FolderWithTrailing
        folderStructure={folderStructure}
        onClick={handleClickFolder}
        renderTrailing={renderTrailing}
        maxWidth={maxWidth}
        isSelected={isCurrentDirectory}
        isExpanded={isExpanded}
      >
        <span
          className="shrink-0 py-[12px] h-[40px] pr-[10px] flex items-center select-none pl-[var(--leftPadding)]"
          onClick={subfolders.length ? handleClickCaret : undefined}
          style={
            { '--leftPadding': `${15 * (level + 1) + 5}px` } as CSSProperties
          }
        >
          {subfolders.length ? (
            <Icon
              icon="file-expand"
              size="xs"
              color="font-primary"
              className={`transition-transform duration-300 ease-in-out${isExpanded ? ' fa-rotate-90' : ''}`}
            />
          ) : (
            <span className="w-[5px] inline-block" />
          )}
        </span>
        <Icon
          icon={
            isCurrentDirectory
              ? 'file-folder-open-active'
              : isCurrentDirectoryPredecessor
                ? 'file-folder-closed-active'
                : 'file-folder-closed'
          }
          color="button-secondary-default"
          size="base"
          className="shrink-0"
        />
        <Typography
          color="button-secondary-default"
          weight={
            isCurrentDirectory || isCurrentDirectoryPredecessor
              ? 'semibold'
              : 'regular'
          }
          truncate
          className="shrink min-w-0 pl-[8px]"
        >
          {name}
        </Typography>
        <Typography
          color="button-secondary-default"
          weight={
            isCurrentDirectory || isCurrentDirectoryPredecessor
              ? 'semibold'
              : 'regular'
          }
          fontStyle="italic"
          className="shrink-0 pl-[4px]"
        >
          {`(${count ?? 0})`}
        </Typography>
      </FolderWithTrailing>
      <div
        className={`grid transition-[grid-template-rows] duration-300 ${isExpanded ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'}`}
      >
        <div className="overflow-hidden">
          {subfolders.map((subfolderStructure) => {
            return (
              <TreeFolder
                key={subfolderStructure.id}
                folderStructure={subfolderStructure}
                selectedFolderId={selectedFolderId}
                onChangeSelected={onChangeSelected}
                expandedFolderIds={expandedFolderIds}
                onChangeExpanded={onChangeExpanded}
                renderTrailing={renderTrailing}
                maxWidth={maxWidth}
              />
            );
          })}
        </div>
      </div>
    </>
  );
};
